import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../utils/firebase';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../utils/firebase';
import Logo from '../components/Logo';
import HomeVenueSelector from '../components/HomeVenueSelector';
import TeamNavbar from '../components/TeamNavbar';
import { useLocation, useNavigate } from 'react-router-dom';
import { checkFoundersClub, checkFounder, checkCompanyFounder, checkAchievements, getAchievementInfo } from '../utils/achievements';
import ImageUploader from '../components/ImageUploader';
import { reviewProfileContent, shouldAutoReject, getModerationDisplay } from '../utils/aiModeration';
import BadgeDisplay from '../components/BadgeDisplay';
import PasswordChangeModal from '../components/PasswordChangeModal';

const TeamProfile = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(null); // Removed unused variable
  // const [activeTab, setActiveTab] = useState('overview'); // Removed unused variable
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [leagueId, setLeagueId] = useState('');
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showJoinTeam, setShowJoinTeam] = useState(false);
  const [joinLeagueId, setJoinLeagueId] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);

  // Form state for creating/editing team
  const [teamForm, setTeamForm] = useState({
    name: '',
    description: '',
    colors: { primary: '#10B981', secondary: '#1F2937' },
    maxMembers: 10,
    homeVenue: ''
  });

  // Enhanced analytics calculation with league differentiation
  const calculateAnalytics = useCallback((gameResults) => {
    if (gameResults.length === 0) {
      return {
        winLossRatio: { wins: 0, losses: 0, ratio: 0 },
        highestScore: 0,
        longestStreak: 0,
        bestRanking: 0,
        lastGameDate: null,
        avgGamesPerWeek: 0,
        avgGamesPerMonth: 0,
        categoryPerformance: {},
        monthlyPerformance: {},
        seasonalTrends: {},
        venuePerformance: {},
        mostFrequentVenues: [],
        hostPerformance: {},
        mostFrequentHosts: [],
        homeVenuePerformance: {
          games: 0,
          totalScore: 0,
          avgScore: 0,
          wins: 0,
          winRate: 0,
          bestScore: 0,
          bestRanking: Infinity
        },
        awayVenuePerformance: {
          games: 0,
          totalScore: 0,
          avgScore: 0,
          wins: 0,
          winRate: 0,
          bestScore: 0,
          bestRanking: Infinity
        },
        venueComparison: {
          homeAdvantage: 0,
          awayPerformance: 0,
          preferredVenues: [],
          avoidVenues: []
        },
        // League differentiation
        leagueStats: {
          officialGames: 0,
          marketplaceGames: 0,
          personalGames: 0,
          officialGPA: 0,
          marketplaceGPA: 0,
          personalGPA: 0,
          officialWins: 0,
          marketplaceWins: 0,
          personalWins: 0
        }
      };
    }

    // Win/Loss ratio
    let wins = 0;
    let losses = 0;
    let currentStreak = 0;
    let longestStreak = 0;
    let bestRanking = Infinity;
    let highestScore = 0;
    let lastGameDate = null;

    // League differentiation tracking
    let officialGames = 0;
    let marketplaceGames = 0;
    let personalGames = 0;
    let officialGPA = 0;
    let marketplaceGPA = 0;
    let personalGPA = 0;
    let officialWins = 0;
    let marketplaceWins = 0;
    let personalWins = 0;

    // Monthly and seasonal tracking
    const monthlyPerformance = {};
    const seasonalTrends = {};
    const venuePerformance = {};
    const venueFrequency = {};
    const hostPerformance = {};
    const hostFrequency = {};

    // Home venue tracking
    const homeVenueId = team?.homeVenue;
    let homeVenueStats = {
      games: 0,
      totalScore: 0,
      avgScore: 0,
      wins: 0,
      winRate: 0,
      bestScore: 0,
      bestRanking: Infinity
    };
    let awayVenueStats = {
      games: 0,
      totalScore: 0,
      avgScore: 0,
      wins: 0,
      winRate: 0,
      bestScore: 0,
      bestRanking: Infinity
    };

    gameResults.forEach((game, index) => {
      const teamResult = game.teamResult;
      const gameDate = game.gameDate;
      
      // Update highest score
      if (teamResult.finalScore > highestScore) {
        highestScore = teamResult.finalScore;
      }

      // Update last game date
      if (!lastGameDate || gameDate > lastGameDate) {
        lastGameDate = gameDate;
      }

      // Calculate ranking (assuming teams are sorted by score in descending order)
      const teamRanking = game.teams.findIndex(t => t.id === teamResult.id) + 1;
      if (teamRanking < bestRanking) {
        bestRanking = teamRanking;
      }

      // Win/Loss calculation (assuming 1st place is a win)
      const isWin = teamRanking === 1;
      if (isWin) {
        wins++;
        currentStreak++;
        if (currentStreak > longestStreak) {
          longestStreak = currentStreak;
        }
      } else {
        losses++;
        currentStreak = 0;
      }

      // League differentiation tracking
      const gameData = game.gameData;
      if (gameData?.isOfficialGame) {
        officialGames++;
        officialGPA += teamResult.gpa || 0;
        if (isWin) officialWins++;
      } else if (gameData?.isMarketplaceGame) {
        marketplaceGames++;
        marketplaceGPA += teamResult.gpa || 0;
        if (isWin) marketplaceWins++;
      } else {
        personalGames++;
        personalGPA += teamResult.gpa || 0;
        if (isWin) personalWins++;
      }

      // Host performance tracking
      if (game.hostId) {
        const hostId = game.hostId;
        const hostName = game.hostName || 'Unknown Host';
        
        if (!hostPerformance[hostId]) {
          hostPerformance[hostId] = {
            name: hostName,
            games: 0,
            totalScore: 0,
            avgScore: 0,
            wins: 0,
            bestRanking: Infinity,
            bestScore: 0
          };
        }
        
        hostPerformance[hostId].games++;
        hostPerformance[hostId].totalScore += teamResult.finalScore;
        hostPerformance[hostId].avgScore = hostPerformance[hostId].totalScore / hostPerformance[hostId].games;
        
        if (isWin) {
          hostPerformance[hostId].wins++;
        }
        
        if (teamRanking < hostPerformance[hostId].bestRanking) {
          hostPerformance[hostId].bestRanking = teamRanking;
        }
        
        if (teamResult.finalScore > hostPerformance[hostId].bestScore) {
          hostPerformance[hostId].bestScore = teamResult.finalScore;
        }

        // Track host frequency
        hostFrequency[hostId] = (hostFrequency[hostId] || 0) + 1;
      }

      // Venue performance tracking
      if (game.venueId) {
        const venueId = game.venueId;
        const venueName = game.venueName || 'Unknown Venue';
        
        if (!venuePerformance[venueId]) {
          venuePerformance[venueId] = {
            name: venueName,
            games: 0,
            totalScore: 0,
            avgScore: 0,
            wins: 0,
            bestRanking: Infinity,
            bestScore: 0
          };
        }
        
        venuePerformance[venueId].games++;
        venuePerformance[venueId].totalScore += teamResult.finalScore;
        venuePerformance[venueId].avgScore = venuePerformance[venueId].totalScore / venuePerformance[venueId].games;
        
        if (isWin) {
          venuePerformance[venueId].wins++;
        }
        
        if (teamRanking < venuePerformance[venueId].bestRanking) {
          venuePerformance[venueId].bestRanking = teamRanking;
        }
        
        if (teamResult.finalScore > venuePerformance[venueId].bestScore) {
          venuePerformance[venueId].bestScore = teamResult.finalScore;
        }

        // Track venue frequency
        venueFrequency[venueId] = (venueFrequency[venueId] || 0) + 1;

        // Home vs Away venue tracking
        if (venueId === homeVenueId) {
          homeVenueStats.games++;
          homeVenueStats.totalScore += teamResult.finalScore;
          if (isWin) homeVenueStats.wins++;
          if (teamResult.finalScore > homeVenueStats.bestScore) {
            homeVenueStats.bestScore = teamResult.finalScore;
          }
          if (teamRanking < homeVenueStats.bestRanking) {
            homeVenueStats.bestRanking = teamRanking;
          }
        } else {
          awayVenueStats.games++;
          awayVenueStats.totalScore += teamResult.finalScore;
          if (isWin) awayVenueStats.wins++;
          if (teamResult.finalScore > awayVenueStats.bestScore) {
            awayVenueStats.bestScore = teamResult.finalScore;
          }
          if (teamRanking < awayVenueStats.bestRanking) {
            awayVenueStats.bestRanking = teamRanking;
          }
        }
      }

      // Monthly performance tracking
      const monthKey = `${gameDate.getFullYear()}-${String(gameDate.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyPerformance[monthKey]) {
        monthlyPerformance[monthKey] = {
          games: 0,
          totalScore: 0,
          avgScore: 0,
          wins: 0,
          bestScore: 0,
          bestRanking: Infinity
        };
      }
      
      monthlyPerformance[monthKey].games++;
      monthlyPerformance[monthKey].totalScore += teamResult.finalScore;
      monthlyPerformance[monthKey].avgScore = monthlyPerformance[monthKey].totalScore / monthlyPerformance[monthKey].games;
      
      if (isWin) {
        monthlyPerformance[monthKey].wins++;
      }
      
      if (teamResult.finalScore > monthlyPerformance[monthKey].bestScore) {
        monthlyPerformance[monthKey].bestScore = teamResult.finalScore;
      }
      
      if (teamRanking < monthlyPerformance[monthKey].bestRanking) {
        monthlyPerformance[monthKey].bestRanking = teamRanking;
      }

      // Seasonal trends
      const season = getSeason(gameDate);
      if (!seasonalTrends[season]) {
        seasonalTrends[season] = {
          games: 0,
          totalScore: 0,
          avgScore: 0,
          wins: 0,
          bestScore: 0,
          bestRanking: Infinity
        };
      }
      
      seasonalTrends[season].games++;
      seasonalTrends[season].totalScore += teamResult.finalScore;
      seasonalTrends[season].avgScore = seasonalTrends[season].totalScore / seasonalTrends[season].games;
      
      if (isWin) {
        seasonalTrends[season].wins++;
      }
      
      if (teamResult.finalScore > seasonalTrends[season].bestScore) {
        seasonalTrends[season].bestScore = teamResult.finalScore;
      }
      
      if (teamRanking < seasonalTrends[season].bestRanking) {
        seasonalTrends[season].bestRanking = teamRanking;
      }
    });

    // Calculate win rate
    const totalGames = wins + losses;
    const winRate = totalGames > 0 ? (wins / totalGames) * 100 : 0;

    // Calculate home venue stats
    if (homeVenueStats.games > 0) {
      homeVenueStats.avgScore = homeVenueStats.totalScore / homeVenueStats.games;
      homeVenueStats.winRate = (homeVenueStats.wins / homeVenueStats.games) * 100;
    }

    // Calculate away venue stats
    if (awayVenueStats.games > 0) {
      awayVenueStats.avgScore = awayVenueStats.totalScore / awayVenueStats.games;
      awayVenueStats.winRate = (awayVenueStats.wins / awayVenueStats.games) * 100;
    }

    // Calculate venue comparison
    const venueComparison = {
      homeAdvantage: homeVenueStats.games > 0 ? homeVenueStats.avgScore - awayVenueStats.avgScore : 0,
      awayPerformance: awayVenueStats.games > 0 ? awayVenueStats.avgScore : 0,
      preferredVenues: Object.entries(venueFrequency)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([venueId]) => venueId),
      avoidVenues: Object.entries(venuePerformance)
        .filter(([, stats]) => stats.games >= 3 && stats.avgScore < 50)
        .map(([venueId]) => venueId)
    };

    // Calculate average games per week/month
    const now = new Date();
    const weeksSinceFirstGame = lastGameDate ? (now - lastGameDate) / (1000 * 60 * 60 * 24 * 7) : 0;
    const monthsSinceFirstGame = lastGameDate ? (now - lastGameDate) / (1000 * 60 * 60 * 24 * 30) : 0;
    
    const avgGamesPerWeek = weeksSinceFirstGame > 0 ? totalGames / weeksSinceFirstGame : 0;
    const avgGamesPerMonth = monthsSinceFirstGame > 0 ? totalGames / monthsSinceFirstGame : 0;

    // Get most frequent venues and hosts
    const mostFrequentVenues = Object.entries(venueFrequency)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([venueId, count]) => ({
        id: venueId,
        name: venuePerformance[venueId]?.name || 'Unknown Venue',
        games: count,
        avgScore: venuePerformance[venueId]?.avgScore || 0
      }));

    const mostFrequentHosts = Object.entries(hostFrequency)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([hostId, count]) => ({
        id: hostId,
        name: hostPerformance[hostId]?.name || 'Unknown Host',
        games: count,
        avgScore: hostPerformance[hostId]?.avgScore || 0
      }));

    // Calculate league-specific averages
    const leagueStats = {
      officialGames,
      marketplaceGames,
      personalGames,
      officialGPA: officialGames > 0 ? officialGPA / officialGames : 0,
      marketplaceGPA: marketplaceGames > 0 ? marketplaceGPA / marketplaceGames : 0,
      personalGPA: personalGames > 0 ? personalGPA / personalGames : 0,
      officialWins,
      marketplaceWins,
      personalWins,
      officialWinRate: officialGames > 0 ? (officialWins / officialGames) * 100 : 0,
      marketplaceWinRate: marketplaceGames > 0 ? (marketplaceWins / marketplaceGames) * 100 : 0,
      personalWinRate: personalGames > 0 ? (personalWins / personalGames) * 100 : 0
    };

    return {
      winLossRatio: { wins, losses, ratio: winRate },
      highestScore,
      longestStreak,
      bestRanking: bestRanking === Infinity ? 0 : bestRanking,
      lastGameDate,
      avgGamesPerWeek,
      avgGamesPerMonth,
      categoryPerformance: {},
      monthlyPerformance,
      seasonalTrends,
      venuePerformance,
      mostFrequentVenues,
      hostPerformance,
      mostFrequentHosts,
      homeVenuePerformance: homeVenueStats,
      awayVenuePerformance: awayVenueStats,
      venueComparison,
      leagueStats
    };
  }, [team]);

  const loadTeamAnalytics = useCallback(async () => {
    if (!team) return;
    
    try {
      setAnalyticsLoading(true);
      console.log('🔍 Loading analytics for team:', team.id);
      
      // Get all game results for this team
      // Try different query approaches to find game results
      let gameResults = [];
      
      // Approach 1: Query by team ID in teams array
      try {
        const gameResultsQuery = query(
          collection(db, 'gameResults'),
          where('teams', 'array-contains', { id: team.id })
        );
        const gameResultsSnapshot = await getDocs(gameResultsQuery);
        console.log('📊 Found game results (approach 1):', gameResultsSnapshot.docs.length);
        
        gameResultsSnapshot.forEach(doc => {
          const data = doc.data();
          const teamResult = data.teams?.find(t => t.id === team.id);
          if (teamResult) {
            gameResults.push({
              ...data,
              teamResult,
              gameDate: data.completedAt?.toDate?.() || new Date(data.completedAt)
            });
          }
        });
      } catch (error) {
        console.log('❌ Approach 1 failed:', error.message);
      }
      
      // Approach 2: Query by team ID in teamIds array (if it exists)
      if (gameResults.length === 0) {
        try {
          const gameResultsQuery2 = query(
            collection(db, 'gameResults'),
            where('teamIds', 'array-contains', team.id)
          );
          const gameResultsSnapshot2 = await getDocs(gameResultsQuery2);
          console.log('📊 Found game results (approach 2):', gameResultsSnapshot2.docs.length);
          
          gameResultsSnapshot2.forEach(doc => {
            const data = doc.data();
            const teamResult = data.teams?.find(t => t.id === team.id);
            if (teamResult) {
              gameResults.push({
                ...data,
                teamResult,
                gameDate: data.completedAt?.toDate?.() || new Date(data.completedAt)
              });
            }
          });
        } catch (error) {
          console.log('❌ Approach 2 failed:', error.message);
        }
      }
      
      // Approach 3: Get all game results and filter by team ID
      if (gameResults.length === 0) {
        try {
          const gameResultsQuery3 = query(collection(db, 'gameResults'));
          const gameResultsSnapshot3 = await getDocs(gameResultsQuery3);
          console.log('📊 Total game results found:', gameResultsSnapshot3.docs.length);
          
          gameResultsSnapshot3.forEach(doc => {
            const data = doc.data();
            // Check if this team participated in this game
            const teamResult = data.teams?.find(t => t.id === team.id);
            if (teamResult) {
              gameResults.push({
                ...data,
                teamResult,
                gameDate: data.completedAt?.toDate?.() || new Date(data.completedAt)
              });
            }
          });
        } catch (error) {
          console.log('❌ Approach 3 failed:', error.message);
        }
      }
      
      console.log('🎯 Final game results for team:', gameResults.length);

      // Sort by date
      gameResults.sort((a, b) => b.gameDate - a.gameDate);

      // Calculate analytics inline to avoid circular dependency
      const analyticsData = calculateAnalytics(gameResults);
      console.log('📈 Calculated analytics:', analyticsData);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading team analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [team, calculateAnalytics]);

  const loadTeamData = useCallback(async () => {
    try {
      console.log('🔍 Loading team data for user:', user?.uid);
      
      // Check if user is already on a team
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      console.log('👤 User data:', userData);
      
      if (userData?.teamId) {
        console.log('🏆 User has team ID:', userData.teamId);
        const teamDoc = await getDoc(doc(db, 'teams', userData.teamId));
        if (teamDoc.exists()) {
          const teamData = teamDoc.data();
          console.log('✅ Team data loaded:', teamData);
          
          // Update existing teams to have 10 member limit
          if (teamData.maxMembers === 6) {
            await updateDoc(doc(db, 'teams', userData.teamId), {
              maxMembers: 10
            });
            teamData.maxMembers = 10;
          }
          
          setTeam(teamData);
          setLeagueId(userData.leagueId || '');
          
          // Don't call loadTeamAnalytics here - let the useEffect handle it
        } else {
          console.log('❌ Team document does not exist');
        }
      } else {
        console.log('👤 User has no team, showing create/join options');
        // Check URL to determine if we should show create or join
        if (location.pathname === '/team/join') {
          setShowJoinTeam(true);
        } else {
          setShowCreateTeam(true);
        }
      }
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, location.pathname]);

  useEffect(() => {
    if (user) {
      loadTeamData();
    }
  }, [user, loadTeamData]);

  // Load analytics when team changes
  useEffect(() => {
    if (team && team.id) {
      console.log('🔄 Team changed, loading analytics for:', team.id);
      loadTeamAnalytics();
    }
  }, [team, loadTeamAnalytics]);

  const getSeason = (date) => {
    const month = date.getMonth();
    if (month >= 2 && month <= 4) return 'Spring';
    if (month >= 5 && month <= 7) return 'Summer';
    if (month >= 8 && month <= 10) return 'Fall';
    return 'Winter';
  };

  const generateLeagueId = async () => {
    try {
      // Query all teams to find the highest league ID
      const teamsQuery = query(collection(db, 'teams'));
      const teamsSnapshot = await getDocs(teamsQuery);
      
      let highestId = 0;
      teamsSnapshot.forEach((doc) => {
        const teamData = doc.data();
        if (teamData.leagueId) {
          // Extract numeric part from league ID (remove any prefix if exists)
          const numericPart = parseInt(teamData.leagueId.replace(/\D/g, ''));
          if (!isNaN(numericPart) && numericPart > highestId) {
            highestId = numericPart;
          }
        }
      });
      
      // Return the next sequential ID
      return (highestId + 1).toString();
    } catch (error) {
      console.error('Error generating league ID:', error);
      // Fallback to timestamp-based ID if there's an error
      return Date.now().toString().slice(-8);
    }
  };

  const createTeam = async () => {
    if (!teamForm.name.trim()) return;

    try {
      // AI Content Moderation
      const moderationResult = await reviewProfileContent({
        fullName: teamForm.name,
        bio: teamForm.description,
        teamName: teamForm.name
      });
      
      // Check if content should be auto-rejected
      if (shouldAutoReject(moderationResult)) {
        const display = getModerationDisplay(moderationResult);
        alert(`❌ ${display.text}: ${moderationResult.recommendations}`);
        return;
      }

      const newLeagueId = await generateLeagueId();
      const teamId = `team_${Date.now()}`;
      
      // Check if team qualifies for Founder's Club
      const isFounder = await checkFoundersClub(teamId, db);
      
      const newTeam = {
        id: teamId,
        name: teamForm.name,
        description: teamForm.description,
        colors: teamForm.colors,
        captain: user.uid,
        members: [user.uid],
        memberDetails: {
          [user.uid]: {
            name: user.displayName || user.email.split('@')[0],
            category: '',
            email: user.email,
            isCaptain: true
          }
        },
        maxMembers: teamForm.maxMembers,
        leagueId: newLeagueId,
        gpa: 0,
        gamesPlayed: 0,
        totalPoints: 0,
        ranking: 0,
        achievements: isFounder ? ['founders_club'] : [],
        createdAt: new Date(),
        swagItems: [],
        profileImageUrl: null,
        homeVenue: teamForm.homeVenue,
        // AI Moderation data
        moderation: {
          ...moderationResult,
          autoRejected: shouldAutoReject(moderationResult)
        }
      };

      // Create team document
      await setDoc(doc(db, 'teams', teamId), newTeam);
      
      // Update user document
      await updateDoc(doc(db, 'users', user.uid), {
        teamId: teamId,
        leagueId: newLeagueId,
        role: 'team_captain'
      });

      setTeam(newTeam);
      setLeagueId(newLeagueId);
      setShowCreateTeam(false);
      setEditing(false);
      
      // Show Founder's Club notification
      if (isFounder) {
        alert('🎉 Congratulations! You\'ve earned the Founder\'s Club badge for being one of the first 100 teams!');
      }
    } catch (error) {
      console.error('Error creating team:', error);
    }
  };

  const joinTeam = async () => {
    if (!joinLeagueId.trim()) return;

    try {
      // Find team by league ID
      const teamQuery = query(collection(db, 'teams'), where('leagueId', '==', joinLeagueId.toUpperCase()));
      const teamSnapshot = await getDocs(teamQuery);
      
      if (teamSnapshot.empty) {
        alert('Team not found. Please check the League ID.');
        return;
      }

      const teamDoc = teamSnapshot.docs[0];
      const teamData = teamDoc.data();
      
      if (teamData.members.length >= teamData.maxMembers) {
        alert('Team is full. Cannot join.');
        return;
      }

      if (teamData.members.includes(user.uid)) {
        alert('You are already a member of this team.');
        return;
      }

      // Add user to team
      const updatedMembers = [...teamData.members, user.uid];
      await updateDoc(doc(db, 'teams', teamDoc.id), {
        members: updatedMembers
      });

      // Update user document
      await updateDoc(doc(db, 'users', user.uid), {
        teamId: teamDoc.id,
        leagueId: teamData.leagueId,
        role: 'team_member'
      });

      setTeam({ ...teamData, id: teamDoc.id, members: updatedMembers });
      setLeagueId(teamData.leagueId);
      setShowJoinTeam(false);
    } catch (error) {
      console.error('Error joining team:', error);
    }
  };

  const dissolveTeam = async () => {
    if (user.uid !== team.captain) {
      alert('Only the team captain can dissolve the team.');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to dissolve the team? This action cannot be undone and will:\n\n' +
      '• Remove all members from the team\n' +
      '• Delete the team from the league\n' +
      '• Reset all team statistics and achievements\n\n' +
      'This is the only way to remove a team captain.'
    );

    if (!confirmed) return;

    try {
      // Remove team association from all members
      const memberUpdatePromises = team.members.map(memberId =>
        updateDoc(doc(db, 'users', memberId), {
          teamId: null,
          leagueId: null,
          role: null
        })
      );

      await Promise.all(memberUpdatePromises);

      // Delete the team document
      await deleteDoc(doc(db, 'teams', team.id));

      // Redirect to team dashboard to create/join a new team
      window.location.href = '/team/dashboard';
    } catch (error) {
      console.error('Error dissolving team:', error);
      alert('Error dissolving team. Please try again.');
    }
  };

  const updateTeam = async () => {
    try {
      // AI Content Moderation for updates
      const moderationResult = await reviewProfileContent({
        fullName: teamForm.name,
        bio: teamForm.description,
        teamName: teamForm.name
      });
      
      // Check if content should be auto-rejected
      if (shouldAutoReject(moderationResult)) {
        const display = getModerationDisplay(moderationResult);
        alert(`❌ ${display.text}: ${moderationResult.recommendations}`);
        return;
      }

      await updateDoc(doc(db, 'teams', team.id), {
        name: teamForm.name,
        description: teamForm.description,
        colors: teamForm.colors,
        // Update moderation data
        moderation: {
          ...moderationResult,
          autoRejected: shouldAutoReject(moderationResult),
          updatedAt: new Date()
        }
      });

      setTeam({ ...team, ...teamForm });
      setEditing(false);
    } catch (error) {
      console.error('Error updating team:', error);
    }
  };

  const deleteTeamImage = async () => {
    if (!team || !team.profileImageUrl) return;

    try {
      setUploadingImage(true);
      
      // Delete from storage
      const imageRef = ref(storage, team.profileImageUrl);
      await deleteObject(imageRef);

      // Update team document
      await updateDoc(doc(db, 'teams', team.id), {
        profileImageUrl: null
      });

      setTeam({ ...team, profileImageUrl: null });
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Error deleting image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const checkAndAwardAchievements = useCallback(async () => {
    if (!team || !user) return;
    
    try {
      const newAchievements = checkAchievements(team);
      
      // Check for Founder badge
      if (checkFounder(user.uid) && !team.achievements?.includes('founder')) {
        newAchievements.push('founder');
      }
      
      // Check for Company Founder badge
      if (checkCompanyFounder(user.uid, user.email) && !team.achievements?.includes('company_founder')) {
        newAchievements.push('company_founder');
      }
      
      if (newAchievements.length > 0) {
        // Update team with new achievements
        const updatedAchievements = [...(team.achievements || []), ...newAchievements];
        await updateDoc(doc(db, 'teams', team.id), {
          achievements: updatedAchievements
        });
        
        // Update local state
        setTeam({ ...team, achievements: updatedAchievements });
        
        // Show notification for new achievements
        newAchievements.forEach(achievementId => {
          const achievement = getAchievementInfo(achievementId);
          if (achievement) {
            alert(`🎉 Achievement Unlocked: ${achievement.name} - ${achievement.description}`);
          }
        });
      }
    } catch (error) {
      console.error('Error checking achievements:', error);
    }
  }, [team, user]);

  useEffect(() => {
    if (team && user) {
      checkAndAwardAchievements();
    }
  }, [team, user, checkAndAwardAchievements]);

  const handleEditToggle = () => {
    if (!editing) {
      // Populate form with existing team data when entering edit mode
      setTeamForm({
        name: team.name || '',
        description: team.description || '',
        colors: team.colors || { primary: '#10B981', secondary: '#1F2937' },
        maxMembers: team.maxMembers || 10,
        homeVenue: team.homeVenue || ''
      });
    }
    setEditing(!editing);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading team profile...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Logo className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Authentication Required</h1>
          <p className="text-gray-400 mb-6">Please log in to access your team profile.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  if (showCreateTeam) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="container mx-auto px-8 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <Logo className="h-32 mx-auto mb-8 text-white" />
              <h1 className="text-6xl font-bold text-white mb-6">Create Your Team</h1>
              <p className="text-2xl text-gray-400">Start your trivia journey with a team!</p>
            </div>

            <div className="bg-gray-800 rounded-2xl p-12 border border-gray-700 shadow-2xl">
              <div className="space-y-8">
                <div>
                  <label className="block text-white text-xl font-semibold mb-4">Team Name</label>
                  <input
                    type="text"
                    value={teamForm.name}
                    onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                    className="w-full p-6 text-xl bg-gray-700 text-white rounded-xl border border-gray-600 focus:border-green-500 focus:outline-none"
                    placeholder="Enter team name"
                  />
                </div>

                <div>
                  <label className="block text-white text-xl font-semibold mb-4">Description</label>
                  <textarea
                    value={teamForm.description}
                    onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                    className="w-full p-6 text-xl bg-gray-700 text-white rounded-xl border border-gray-600 focus:border-green-500 focus:outline-none"
                    rows="5"
                    placeholder="Tell us about your team..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <label className="block text-white text-xl font-semibold mb-4">Primary Color</label>
                    <input
                      type="color"
                      value={teamForm.colors.primary}
                      onChange={(e) => setTeamForm({
                        ...teamForm,
                        colors: { ...teamForm.colors, primary: e.target.value }
                      })}
                      className="w-full h-20 rounded-xl border border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-xl font-semibold mb-4">Secondary Color</label>
                    <input
                      type="color"
                      value={teamForm.colors.secondary}
                      onChange={(e) => setTeamForm({
                        ...teamForm,
                        colors: { ...teamForm.colors, secondary: e.target.value }
                      })}
                      className="w-full h-20 rounded-xl border border-gray-600"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white text-xl font-semibold mb-4">Home Venue</label>
                  <HomeVenueSelector
                    value={teamForm.homeVenue}
                    onChange={(value) => setTeamForm({ ...teamForm, homeVenue: value })}
                  />
                </div>

                <button
                  onClick={createTeam}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold text-2xl py-6 px-8 rounded-xl transition duration-200 shadow-lg"
                >
                  Create Team
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showJoinTeam) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="container mx-auto px-8 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <Logo className="h-32 mx-auto mb-8 text-white" />
              <h1 className="text-6xl font-bold text-white mb-6">Join a Team</h1>
              <p className="text-2xl text-gray-400">Enter the League ID to join a team!</p>
            </div>

            <div className="bg-gray-800 rounded-2xl p-12 border border-gray-700 shadow-2xl">
              <div className="space-y-8">
                <div>
                  <label className="block text-white text-xl font-semibold mb-4">League ID</label>
                  <input
                    type="text"
                    value={joinLeagueId}
                    onChange={(e) => setJoinLeagueId(e.target.value)}
                    className="w-full p-6 text-xl bg-gray-700 text-white rounded-xl border border-gray-600 focus:border-green-500 focus:outline-none"
                    placeholder="Enter League ID"
                  />
                </div>

                <button
                  onClick={joinTeam}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold text-2xl py-6 px-8 rounded-xl transition duration-200 shadow-lg"
                >
                  Join Team
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Team Navigation Bar */}
      <TeamNavbar />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Logo className="h-16 mx-auto mb-4 text-white" />
            <h1 className="text-3xl font-bold text-white mb-2">{team.name}</h1>
            <p className="text-gray-400">League ID: {leagueId}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Team Stats */}
            <div className="lg:col-span-2 space-y-6">
              {/* Team Profile Picture */}
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">Team Profile Picture</h2>
                  {user.uid === team.captain && (
                    <button
                      onClick={handleEditToggle}
                      className="text-green-500 hover:text-green-400 text-sm"
                    >
                      {editing ? 'Cancel' : 'Edit'}
                    </button>
                  )}
                </div>

                <div className="flex items-center space-x-6">
                  <div className="flex-shrink-0">
                    {team.profileImageUrl ? (
                      <img
                        src={team.profileImageUrl}
                        alt={`${team.name} profile`}
                        className="w-24 h-24 rounded-full object-cover border-2 border-gray-600"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center">
                        <span className="text-gray-400 text-2xl">🏆</span>
                      </div>
                    )}
                  </div>

                  {editing && user.uid === team.captain && (
                    <div className="flex-1 space-y-3">
                      <ImageUploader
                        label="Upload Team Picture"
                        storagePath={`team-profiles/${team.id}`}
                        onUpload={async (url) => {
                          await updateDoc(doc(db, 'teams', team.id), { profileImageUrl: url });
                          setTeam({ ...team, profileImageUrl: url });
                        }}
                      />
                      {team.profileImageUrl && (
                        <button
                          onClick={deleteTeamImage}
                          disabled={uploadingImage}
                          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-4 py-2 rounded text-sm"
                        >
                          {uploadingImage ? 'Deleting...' : 'Remove Picture'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* GPA and Ranking */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Team Performance</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-500">{team.gpa.toFixed(2)}</div>
                    <div className="text-gray-400 text-sm">GPA</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-500">#{team.ranking}</div>
                    <div className="text-gray-400 text-sm">Ranking</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-500">{team.gamesPlayed}</div>
                    <div className="text-gray-400 text-sm">Games Played</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-500">{team.totalPoints}</div>
                    <div className="text-gray-400 text-sm">Lifetime Points</div>
                  </div>
                </div>
              </div>

              {/* Team Analytics */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Team Analytics</h2>
                
                {/* Performance Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-500">{team.members.length}</div>
                    <div className="text-gray-400 text-sm">Team Size</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-pink-500">
                      {team.gamesPlayed > 0 ? (team.totalPoints / team.gamesPlayed).toFixed(1) : '0'}
                    </div>
                    <div className="text-gray-400 text-sm">Avg Points/Game</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-500">
                      {team.achievements?.length || 0}
                    </div>
                    <div className="text-gray-400 text-sm">Badges Earned</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-teal-500">
                      {team.createdAt ? Math.floor((new Date() - new Date(team.createdAt.toDate ? team.createdAt.toDate() : team.createdAt)) / (1000 * 60 * 60 * 24)) : 0}
                    </div>
                    <div className="text-gray-400 text-sm">Days Active</div>
                  </div>
                </div>

                {/* Analytics Section */}
                {analyticsLoading ? (
                  <div className="bg-gray-700 rounded-lg p-8 mb-6">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                      <p className="text-gray-300">Loading team analytics...</p>
                    </div>
                  </div>
                ) : analytics ? (
                  <>
                    {/* Win/Loss and Streaks */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-500">{analytics?.winLossRatio?.wins || 0}</div>
                        <div className="text-gray-400 text-sm">Wins</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-500">{analytics?.winLossRatio?.losses || 0}</div>
                        <div className="text-gray-400 text-sm">Losses</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-500">{analytics?.winLossRatio?.ratio || 0}%</div>
                        <div className="text-gray-400 text-sm">Win Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-500">{analytics?.longestStreak || 0}</div>
                        <div className="text-gray-400 text-sm">Best Streak</div>
                      </div>
                    </div>

                    {/* Records and Rankings */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-500">{analytics?.highestScore || 0}</div>
                        <div className="text-gray-400 text-sm">Highest Score</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-cyan-500">#{analytics?.bestRanking || 0}</div>
                        <div className="text-gray-400 text-sm">Best Ranking</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-500">{analytics?.avgGamesPerWeek || 0}</div>
                        <div className="text-gray-400 text-sm">Games/Week</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-rose-500">{analytics?.avgGamesPerMonth || 0}</div>
                        <div className="text-gray-400 text-sm">Games/Month</div>
                      </div>
                    </div>

                    {/* Last Game Info */}
                    {analytics?.lastGameDate && (
                      <div className="bg-gray-700 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-2">Last Game</h3>
                        <p className="text-gray-300">
                          {analytics.lastGameDate.toLocaleDateString()} at {analytics.lastGameDate.toLocaleTimeString()}
                        </p>
                      </div>
                    )}

                    {/* Seasonal Performance */}
                    {analytics?.seasonalTrends && Object.keys(analytics.seasonalTrends).length > 0 && (
                      <div className="bg-gray-700 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-3">Seasonal Performance</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {Object.entries(analytics.seasonalTrends).map(([season, data]) => (
                            <div key={season} className="text-center">
                              <div className="text-lg font-bold text-white">{season}</div>
                              <div className="text-sm text-gray-400">
                                {data.games} games • {data.wins} wins
                              </div>
                              <div className="text-sm text-green-400">
                                Avg: {data.avgScore.toFixed(0)} pts
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Monthly Performance Chart */}
                    {analytics?.monthlyPerformance && Object.keys(analytics.monthlyPerformance).length > 0 && (
                      <div className="bg-gray-700 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-3">Monthly Performance</h3>
                        <div className="space-y-2">
                          {Object.entries(analytics.monthlyPerformance)
                            .sort(([a], [b]) => b.localeCompare(a))
                            .slice(0, 6)
                            .map(([month, data]) => (
                              <div key={month} className="flex justify-between items-center">
                                <span className="text-gray-300 text-sm">
                                  {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                </span>
                                <div className="flex items-center space-x-4">
                                  <span className="text-gray-400 text-sm">{data.games} games</span>
                                  <span className="text-green-400 text-sm">{data.wins} wins</span>
                                  <span className="text-blue-400 text-sm">{data.avgScore.toFixed(0)} avg</span>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Venue Performance */}
                    {analytics?.mostFrequentVenues && analytics.mostFrequentVenues.length > 0 && (
                      <div className="bg-gray-700 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-3">Most Frequent Venues</h3>
                        <div className="space-y-3">
                          {analytics.mostFrequentVenues.map((venue) => (
                            <div key={venue.id} className="flex justify-between items-center">
                              <div>
                                <div className="text-white font-medium">{venue.name}</div>
                                <div className="text-gray-400 text-sm">{venue.games} games played</div>
                              </div>
                              <div className="text-right">
                                <div className="text-green-400 text-sm">{venue.wins} wins</div>
                                <div className="text-blue-400 text-sm">{venue.avgScore.toFixed(0)} avg score</div>
                                <div className="text-yellow-400 text-sm">Best: #{venue.bestRanking}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Host Performance */}
                    {analytics?.mostFrequentHosts && analytics.mostFrequentHosts.length > 0 && (
                      <div className="bg-gray-700 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-3">Regular Hosts</h3>
                        <div className="space-y-3">
                          {analytics.mostFrequentHosts.map((host) => (
                            <div key={host.id} className="flex justify-between items-center">
                              <div>
                                <div className="text-white font-medium">{host.name}</div>
                                <div className="text-gray-400 text-sm">{host.games} games hosted</div>
                              </div>
                              <div className="text-right">
                                <div className="text-green-400 text-sm">{host.wins} wins</div>
                                <div className="text-blue-400 text-sm">{host.avgScore.toFixed(0)} avg score</div>
                                <div className="text-yellow-400 text-sm">Best: #{host.bestRanking}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Home vs Away Performance */}
                    {analytics && (analytics.homeVenuePerformance?.games > 0 || analytics.awayVenuePerformance?.games > 0) && (
                      <div className="bg-gray-700 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-3">Home vs Away Performance</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Home Venue Performance */}
                          <div className="bg-gray-800 rounded-lg p-4">
                            <h4 className="text-md font-semibold text-green-400 mb-2">🏠 Home Venue</h4>
                            {analytics.homeVenuePerformance?.games > 0 ? (
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-gray-300 text-sm">Games:</span>
                                  <span className="text-white">{analytics.homeVenuePerformance.games}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-300 text-sm">Win Rate:</span>
                                  <span className="text-green-400">{analytics.homeVenuePerformance.winRate.toFixed(1)}%</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-300 text-sm">Avg Score:</span>
                                  <span className="text-blue-400">{analytics.homeVenuePerformance.avgScore.toFixed(0)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-300 text-sm">Best Score:</span>
                                  <span className="text-purple-400">{analytics.homeVenuePerformance.bestScore}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-300 text-sm">Best Rank:</span>
                                  <span className="text-yellow-400">#{analytics.homeVenuePerformance.bestRanking}</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-400 text-sm">No home venue games yet</p>
                            )}
                          </div>

                          {/* Away Venue Performance */}
                          <div className="bg-gray-800 rounded-lg p-4">
                            <h4 className="text-md font-semibold text-blue-400 mb-2">✈️ Away Venues</h4>
                            {analytics.awayVenuePerformance?.games > 0 ? (
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-gray-300 text-sm">Games:</span>
                                  <span className="text-white">{analytics.awayVenuePerformance.games}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-300 text-sm">Win Rate:</span>
                                  <span className="text-green-400">{analytics.awayVenuePerformance.winRate.toFixed(1)}%</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-300 text-sm">Avg Score:</span>
                                  <span className="text-blue-400">{analytics.awayVenuePerformance.avgScore.toFixed(0)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-300 text-sm">Best Score:</span>
                                  <span className="text-purple-400">{analytics.awayVenuePerformance.bestScore}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-300 text-sm">Best Rank:</span>
                                  <span className="text-yellow-400">#{analytics.awayVenuePerformance.bestRanking}</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-400 text-sm">No away venue games yet</p>
                            )}
                          </div>
                        </div>

                        {/* Home Advantage */}
                        {analytics.homeVenuePerformance?.games > 0 && analytics.awayVenuePerformance?.games > 0 && (
                          <div className="mt-4 p-3 bg-gray-800 rounded-lg">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-300 text-sm">Home Advantage:</span>
                              <span className={`font-semibold ${analytics.venueComparison.homeAdvantage > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {analytics.venueComparison.homeAdvantage > 0 ? '+' : ''}{analytics.venueComparison.homeAdvantage.toFixed(1)} points
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* League Performance Breakdown */}
                    {analytics?.leagueStats && (
                      <div className="bg-gray-700 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-3">League Performance</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Official Games */}
                          <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-3">
                            <h4 className="text-md font-semibold text-blue-400 mb-2">🏆 Official Games</h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-300">Games:</span>
                                <span className="text-white">{analytics.leagueStats.officialGames}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Wins:</span>
                                <span className="text-green-400">{analytics.leagueStats.officialWins}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Win Rate:</span>
                                <span className="text-green-400">{analytics.leagueStats.officialWinRate ? analytics.leagueStats.officialWinRate.toFixed(1) : '0.0'}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Avg GPA:</span>
                                <span className="text-yellow-400">{analytics.leagueStats.officialGPA ? analytics.leagueStats.officialGPA.toFixed(2) : '0.00'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Marketplace Games */}
                          <div className="bg-purple-900/20 border border-purple-600 rounded-lg p-3">
                            <h4 className="text-md font-semibold text-purple-400 mb-2">🛒 Marketplace Games</h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-300">Games:</span>
                                <span className="text-white">{analytics.leagueStats.marketplaceGames}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Wins:</span>
                                <span className="text-green-400">{analytics.leagueStats.marketplaceWins}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Win Rate:</span>
                                <span className="text-green-400">{analytics.leagueStats.marketplaceWinRate ? analytics.leagueStats.marketplaceWinRate.toFixed(1) : '0.0'}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Avg GPA:</span>
                                <span className="text-yellow-400">{analytics.leagueStats.marketplaceGPA ? analytics.leagueStats.marketplaceGPA.toFixed(2) : '0.00'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Personal Games */}
                          <div className="bg-green-900/20 border border-green-600 rounded-lg p-3">
                            <h4 className="text-md font-semibold text-green-400 mb-2">🎮 Personal Games</h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-300">Games:</span>
                                <span className="text-white">{analytics.leagueStats.personalGames}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Wins:</span>
                                <span className="text-green-400">{analytics.leagueStats.personalWins}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Win Rate:</span>
                                <span className="text-green-400">{analytics.leagueStats.personalWinRate ? analytics.leagueStats.personalWinRate.toFixed(1) : '0.0'}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-300">Avg GPA:</span>
                                <span className="text-yellow-400">{analytics.leagueStats.personalGPA ? analytics.leagueStats.personalGPA.toFixed(2) : '0.00'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Venue Preferences */}
                    {(analytics?.venueComparison.preferredVenues.length > 0 || analytics?.venueComparison.avoidVenues.length > 0) && (
                      <div className="bg-gray-700 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-semibold text-white mb-3">Venue Preferences</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Preferred Venues */}
                          {analytics?.venueComparison.preferredVenues.length > 0 && (
                            <div>
                              <h4 className="text-md font-semibold text-green-400 mb-2">🎯 Best Venues</h4>
                              <div className="space-y-2">
                                {analytics.venueComparison.preferredVenues.map((venue, index) => (
                                  <div key={index} className="flex justify-between items-center">
                                    <span className="text-white text-sm">{venue.name}</span>
                                    <span className="text-green-400 text-sm">{venue.avgScore ? venue.avgScore.toFixed(0) : '0'} avg</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Venues to Avoid */}
                          {analytics?.venueComparison.avoidVenues.length > 0 && (
                            <div>
                              <h4 className="text-md font-semibold text-red-400 mb-2">⚠️ Challenging Venues</h4>
                              <div className="space-y-2">
                                {analytics.venueComparison.avoidVenues.map((venue, index) => (
                                  <div key={index} className="flex justify-between items-center">
                                    <span className="text-white text-sm">{venue.name}</span>
                                    <span className="text-red-400 text-sm">{venue.avgScore ? venue.avgScore.toFixed(0) : '0'} avg</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-gray-700 rounded-lg p-8 mb-6">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                      <p className="text-gray-300">No analytics data available yet.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Team Info */}
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">Team Information</h2>
                  <button
                    onClick={handleEditToggle}
                    className="text-green-500 hover:text-green-400 text-sm"
                  >
                    {editing ? 'Cancel' : 'Edit'}
                  </button>
                </div>

                {editing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white mb-2">Team Name</label>
                      <input
                        type="text"
                        value={teamForm.name}
                        onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                        className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-white mb-2">Description</label>
                      <textarea
                        value={teamForm.description}
                        onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                        className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                        rows="3"
                      />
                    </div>
                    <div>
                      <label className="block text-white mb-2">Home Venue</label>
                      <HomeVenueSelector
                        value={teamForm.homeVenue}
                        onChange={(value) => setTeamForm({ ...teamForm, homeVenue: value })}
                      />
                    </div>
                    <button
                      onClick={updateTeam}
                      className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                    >
                      Save Changes
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-300 mb-4">{team.description || 'No description added yet.'}</p>
                    {team.homeVenue && (
                      <p className="text-gray-300 mb-4">
                        <span className="text-gray-400">Home Venue:</span> {team.homeVenue}
                      </p>
                    )}
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: team.colors.primary }}></div>
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: team.colors.secondary }}></div>
                      <span className="text-gray-400 text-sm">Team Colors</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Achievements */}
              <BadgeDisplay 
                achievements={team?.achievements}
                getAchievementInfo={getAchievementInfo}
                title="Team Achievements"
                emptyMessage="No achievements yet. Keep playing to earn badges!"
              />

              {/* Swag Store */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Team Swag</h2>
                <p className="text-gray-400 text-sm mb-4">Customize your team gear with your colors and logo!</p>
                <button 
                  onClick={() => navigate('/team/swag')}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                >
                  Visit Swag Store
                </button>
              </div>

              {/* Account Security */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Account Security</h2>
                <p className="text-gray-400 text-sm mb-4">Manage your account password and security settings</p>
                <button 
                  onClick={() => setShowPasswordChangeModal(true)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  🔑 Change Password
                </button>
              </div>

              {/* Team Management - Captain Only */}
              {user.uid === team.captain && (
                <div className="bg-red-900/20 border border-red-600 rounded-lg p-6">
                  <h2 className="text-xl font-bold text-red-400 mb-4">Team Management</h2>
                  <p className="text-gray-300 text-sm mb-4">
                    As team captain, you have additional management options. Use these carefully as some actions cannot be undone.
                  </p>
                  <button
                    onClick={dissolveTeam}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition duration-200"
                  >
                    🗑️ Dissolve Team
                  </button>
                  <p className="text-red-400 text-xs mt-2">
                    This will permanently delete the team and remove all members. This is the only way to remove a team captain.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={showPasswordChangeModal}
        onClose={() => setShowPasswordChangeModal(false)}
        onSuccess={() => {
          alert('✅ Password updated successfully!');
          setShowPasswordChangeModal(false);
        }}
      />
    </div>
  );
};

export default TeamProfile; 